import React, { useState, useEffect } from "react";
import { isNM, fmtDates } from "../lib/vikas";

export function ReasonChip({ text, testid }) {
  const [open, setOpen] = useState(false);
  const upper = String(text || "").toUpperCase();
  const isPartial = upper.startsWith("PARTIAL");
  const label = isPartial
    ? "partial"
    : upper.startsWith("NO SOURCE YET")
    ? "no source yet"
    : upper.startsWith("NOT YET")
    ? "not yet"
    : "not measured";
  return (
    <span>
      <button
        className={`chip ${isPartial ? "chip-y" : "chip-g"}`}
        data-testid={testid}
        onClick={() => setOpen(!open)}
      >
        {label}
      </button>
      {open && <span className="chip-reason">{fmtDates(text)}</span>}
    </span>
  );
}

export function Val({ v, missing = "not measured", empty = "—" }) {
  if (v === null || v === undefined) return <span className="nm">{missing}</span>;
  if (typeof v === "string") {
    if (v.trim() === "") return <span className="nm">{empty}</span>;
    if (isNM(v)) return <ReasonChip text={v} />;
    return <span>{fmtDates(v)}</span>;
  }
  return <span>{String(v)}</span>;
}

export function Panel({ title, sub, span, children, testid }) {
  const style = {};
  if (span === 2) style.gridColumn = "span 2";
  if (span === 3) style.gridColumn = "1 / -1";
  return (
    <section className="panel" style={style} data-testid={testid}>
      <div className="panel-head">
        <h2 className="panel-title">{title}</h2>
        {sub}
      </div>
      {children}
    </section>
  );
}

export function RowLine({ id, text, meta, dot, textClass, onClick, testid }) {
  return (
    <button className="rowline" data-testid={testid} onClick={onClick}>
      {dot && <span className={`dot ${dot}`} />}
      <span className="min-w-0 flex-1">
        <span className={`rowline-text ${textClass || ""}`}>{text}</span>
        <span className="rowline-meta">
          {id}
          {meta ? ` · ${meta}` : ""}
        </span>
      </span>
    </button>
  );
}

export function ShowMore({ total, shown, onMore, testid, label }) {
  if (total <= shown) return null;
  return (
    <button className="showmore" data-testid={testid} onClick={onMore}>
      {label || `Show all · ${total - shown} more`}
    </button>
  );
}

export function BarChart({ series, testid, showValues = false }) {
  const [sel, setSel] = useState(null);
  const max = Math.max(1, ...series.map((d) => d.count || 0));
  return (
    <div className="bars7" data-testid={testid}>
      {series.map((d, i) => {
        const h = Math.max(((d.count || 0) / max) * 100, d.count > 0 ? 6 : 2);
        return (
          <button
            key={i}
            className="bar-col"
            title={`${d.label}: ${d.count}`}
            data-testid={`${testid}-bar-${i}`}
            onClick={() => setSel(sel === i ? null : i)}
          >
            <span className="bar-val">{showValues || sel === i ? d.count : ""}</span>
            <div className="bar-track-v">
              <div className="bar-fill-v" style={{ height: `${h}%` }} />
            </div>
            <span className="bar-day">{d.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Drawer({ onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="drawer-overlay" data-testid="drawer-overlay" onClick={onClose}>
      <aside className="drawer" data-testid="drawer" onClick={(e) => e.stopPropagation()}>
        <button className="vk-arrow drawer-x" data-testid="drawer-close" onClick={onClose} aria-label="close">✕</button>
        {children}
      </aside>
    </div>
  );
}

export function Collapsed({ title, note, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="panel" data-testid={testid}>
      <button className="collapse-head" data-testid={`${testid}-head`} onClick={() => setOpen(!open)}>
        <span className="panel-title">{title}</span>
        {note && <span className="lbl">{note}</span>}
        <span className="ml-auto" style={{ color: "var(--muted)" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </section>
  );
}

export function docTitle(p) {
  const b = String(p || "").split("/").pop();
  return b.replace(/\.md$/i, "");
}

// Fine white line drawing of solar panel rows in perspective (cover / top band).
export function PanelLines({ className }) {
  const cells = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 6; c++) {
      const x = 320 + c * 96 - r * 70;
      const y = 24 + r * 108;
      cells.push(
        <polygon key={`${r}-${c}`} points={`${x},${y} ${x + 74},${y - 16} ${x + 112},${y + 46} ${x + 38},${y + 62}`} />
      );
    }
  }
  return (
    <svg className={className} viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g fill="none" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="1">
        {cells}
      </g>
    </svg>
  );
}

const LANE_ICONS = {
  alert: "M8 2 L15 14 H1 Z M8 6.5 V9.5 M8 11.8 V12",
  waiting: "M3 2 H13 M3 14 H13 M4.5 2 C4.5 6.5 11.5 6.5 11.5 2 M4.5 14 C4.5 9.5 11.5 9.5 11.5 14",
  gear: "M8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z M8 1.8v2 M8 12.2v2 M1.8 8h2 M12.2 8h2 M3.6 3.6l1.4 1.4 M11 11l1.4 1.4 M12.4 3.6L11 5 M5 11l-1.4 1.4",
  people: "M5.5 8a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z M11 8.6a1.8 1.8 0 1 0 0-3.6 M1.5 13.5c.6-2.4 2.2-3.6 4-3.6s3.4 1.2 4 3.6 M10.6 10.3c1.5.2 2.7 1.3 3.2 3.2",
  mail: "M2 4 H14 V12 H2 Z M2 5 L8 9 L14 5",
  plant: "M8 14 V7 M8 7 C8 4.5 6 3 3.5 3 C3.5 5.5 5.5 7 8 7 Z M8 8.6 C8 6.6 9.7 5.3 12 5.3 C12 7.5 10.3 8.6 8 8.6 Z",
  pause: "M5 3 V13 M11 3 V13",
  build: "M9.5 2.5 L13.5 6.5 L6 14 L2 14 L2 10 Z M8 4 L12 8",
  coin: "M8 14a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z M8 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z",
  book: "M4 2.5 H12.5 V13.5 H4 Z M4 11 H12.5 M7.5 5.5h3 M7.5 7.5h3",
  doubt: "M8 14a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z M6 6.2C6.2 5 7 4.4 8 4.4c1.2 0 2 .7 2 1.7 0 1.3-1.7 1.6-1.7 3 M8 11.6v.2",
  check: "M3 8.5 L6.5 12 L13 4.5",
  send: "M2 8h10 M8.5 3.5 L13.5 8 L8.5 12.5",
};

export function LaneIcon({ name }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="lane-icon">
      <path d={LANE_ICONS[name] || LANE_ICONS.alert} />
    </svg>
  );
}
